import { Component, OnInit, OnDestroy } from '@angular/core';
import { ApexOptions } from 'apexcharts';
import { Spinkit } from 'ng-http-loader';
import { Router } from '@angular/router';
import { BasePageComponent } from '../base-page';
import { Store } from '@ngrx/store';
import { IAppState } from 'src/app/interfaces/app-state';
import { HttpService } from 'src/app/services/http/http.service';

type TimeFilterKey = 'all' | '7d' | '30d' | '90d' | '6m' | '1y';

interface SummaryCard {
  title: string;
  value: number;
  subtitle: string;
}

interface SimpleChartItem {
  label: string;
  value: number;
}

interface MultiSeriesChart {
  categories: string[];
  series: { name: string; data: number[] }[];
}

interface TopDish {
  name: string;
  tag: string;
  restaurant: string;
  rating: number;
  trendValue: number;
}

interface ComplianceItem {
  title: string;
  description: string;
  status: 'compliant' | 'updated' | 'attention';
  updatedOn: string;
}

interface ResourceItem {
  title: string;
  points: string[];
  updatedOn: string;
}

interface CalendarEvent {
  day: number;
  title: string;
  type: 'holiday' | 'event' | 'season' | 'deadline';
  description: string;
}

interface CalendarMonth {
  month: string;
  events: CalendarEvent[];
}

interface DashboardPayload {
  summaryCards: SummaryCard[];
  peopleWithDietaryPreferences: SimpleChartItem[];
  peopleWithFoodAllergies: SimpleChartItem[];
  foodAllergyDistribution: SimpleChartItem[];
  foodPreferenceByCategory: MultiSeriesChart;
  topCuisineTitle: string;
  topCuisineDishes: TopDish[];
  topCategoryTitle: string;
  topCategoryDishes: TopDish[];
  complianceItems: ComplianceItem[];
  resources: ResourceItem[];
  planningMonths: CalendarMonth[];
}

@Component({
  selector: 'main-analytics',
  templateUrl: './main-analytics.component.html',
  styleUrls: ['./main-analytics.component.scss']
})
export class MainAnalyticsPageComponentnt extends BasePageComponent implements OnInit, OnDestroy {
  public spinkit = Spinkit;

  restoId: any;
  readonly timeFilters = [
    { label: 'All Time', value: 'all' as TimeFilterKey },
    { label: 'Last 7 Days', value: '7d' as TimeFilterKey },
    { label: 'Last 30 Days', value: '30d' as TimeFilterKey },
    { label: 'Last 90 Days', value: '90d' as TimeFilterKey },
    { label: 'Last 6 Months', value: '6m' as TimeFilterKey },
    { label: 'Last 1 Year', value: '1y' as TimeFilterKey }
  ];


  selectedTimeFilter: TimeFilterKey = 'all';

  readonly chartColors: string[] = [
    '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444',
    '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#14B8A6',
    '#6366F1', '#22C55E', '#EAB308', '#A855F7', '#F43F5E',
    '#0EA5E9', '#65A30D', '#D97706', '#7C3AED', '#059669'
  ];



  chartsReady = false;
  isLoading = false;

  dashboardData: DashboardPayload = this.getEmptyDashboardPayload();

  dietaryPreferencesChart: ApexOptions = this.createDistributedBarChart([], 'Number of People', 320);
  foodAllergiesChart: ApexOptions = this.createDistributedBarChart([], 'Number of People', 320);
  allergyDistributionChart: ApexOptions = this.createPieChart([], 320);
  preferenceByCategoryChart: ApexOptions = this.createMultiSeriesBarChart(
    {
      categories: [],
      series: [
        { name: 'Vegan', data: [] },
        { name: 'Vegetarian', data: [] }
      ]
    },
    'People Count',
    320
  );

  constructor(
    store: Store<IAppState>,
    httpSv: HttpService,
    private router: Router
  ) {
    super(store, httpSv);
    this.pageData = {
      title: '',
      loaded: true,
      breadcrumbs: [
        {
          title: 'Mainanalytics'
        }
      ]
    };
  }

  ngOnInit(): void {
    this.restoId = localStorage.getItem('restoID');
    super.ngOnInit();
    this.getRestaurantAnalytics();
  }

  onTimeFilterChange(): void {
    let range = null
    if (this.selectedTimeFilter !== 'all') {
      range = this.getEpochRange(this.selectedTimeFilter);
    }
    this.getRestaurantAnalytics(range);
  }


  getEpochRange(filter: TimeFilterKey) {
    const now = new Date();
    let from: number | null = null;

    switch (filter) {
      case '7d':
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).getTime();
        break;

      case '30d':
        from = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).getTime();
        break;

      case '90d':
        from = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).getTime();
        break;

      case '6m': {
        const d = new Date(now);
        d.setMonth(d.getMonth() - 6);
        from = d.getTime();
        break;
      }

      case '1y': {
        const d = new Date(now);
        d.setFullYear(d.getFullYear() - 1);
        from = d.getTime();
        break;
      }

      case 'all':
      default:
        from = null;
        break;
    }

    return from;

    // return {
    //   fromEpoch: from,
    //   toEpoch: now.getTime()
    // };
  }

  getRestaurantAnalytics(range:any = null): void {
    this.isLoading = true;
    this.chartsReady = false;
    this.httpSv.getRestaurantAnalytics(this.restoId, range).subscribe(
      (res: any) => {

        const apiData = res.result && res.result.data ? res.result.data : {};

        this.dashboardData = this.transformApiResponse(apiData);

        this.buildCharts();
        this.chartsReady = true;
        this.isLoading = false;
      },
      (error: any) => {
        console.error('Dashboard API error:', error);
        this.dashboardData = this.getEmptyDashboardPayload();
        this.buildCharts();
        this.chartsReady = true;
        this.isLoading = false;
      }
    );
  }

  private buildCharts(): void {
    this.dietaryPreferencesChart = this.createDistributedBarChart(
      this.dashboardData.peopleWithDietaryPreferences,
      'Number of People',
      320
    );

    this.foodAllergiesChart = this.createDistributedBarChart(
      this.dashboardData.peopleWithFoodAllergies,
      'Number of People',
      320
    );

    this.allergyDistributionChart = this.createPieChart(
      this.dashboardData.foodAllergyDistribution,
      320
    );

    this.preferenceByCategoryChart = this.createMultiSeriesBarChart(
      this.dashboardData.foodPreferenceByCategory,
      'People Count',
      320
    );
  }

  private transformApiResponse(api: any): DashboardPayload {
    return {
      summaryCards: [
        {
          title: 'Number of Diners',
          value: this.toNumber(api?.dashboard?.no_of_diners),
          subtitle: 'Total diners recorded'
        },
        {
          title: 'With Dietary Preferences',
          value: this.toNumber(api?.dashboard?.with_diet_preferences),
          subtitle: 'Vegan, Vegetarian, Halal, etc.'
        },
        {
          title: 'With Food Allergies',
          value: this.toNumber(api?.dashboard?.with_food_allergies),
          subtitle: 'Various allergen types'
        }
      ],

      peopleWithDietaryPreferences: this.mapXYChart(
        api?.dpa_barchart?.dietary_preference_barchart
      ),

      peopleWithFoodAllergies: this.mapXYChart(
        api?.dpa_barchart?.allergens_barchart
      ),

      foodAllergyDistribution: this.mapXYChart(
        api?.fa_piechart
      ),

      foodPreferenceByCategory: {
        categories: this.getStringArray(api?.fpd_barchart?.x),
        series: [
          {
            name: 'Vegan',
            data: this.getNumberArray(api?.fpd_barchart?.vegan)
          },
          {
            name: 'Vegetarian',
            data: this.getNumberArray(api?.fpd_barchart?.vegetarian)
          }
        ]
      },

      topCuisineTitle: api?.tpd_similar_cuisine.cuisine_name || '',
      topCuisineDishes: Array.isArray(api?.tpd_similar_cuisine.dish_review)
        ? api.tpd_similar_cuisine.dish_review.map((item: any) => this.mapTopDish(item))
        : [],

      topCategoryTitle: api?.tpd_similar_category.category_name || '',
      topCategoryDishes: Array.isArray(api?.tpd_similar_category.dish_review)
        ? api.tpd_similar_category.dish_review.map((item: any) => this.mapTopDish(item))
        : [],

      complianceItems: this.mapComplianceItems(
        Array.isArray(api?.regulatory_infos) ? api.regulatory_infos : []
      ),

      resources: this.mapResourcesItems(
        Array.isArray(api?.planning_resources) ? api.planning_resources : []
      ),

      planningMonths: this.groupPlanningCalendar(
        Array.isArray(api?.planning_calendar) ? api.planning_calendar : []
      )
    };
  }

  private mapXYChart(chartData: any): SimpleChartItem[] {
    const x = this.getStringArray(chartData?.x);
    const y = this.getNumberArray(chartData?.y);

    return x.map((label: string, index: number) => ({
      label: label,
      value: this.toNumber(y[index])
    }));
  }

  private mapTopDish(item: any): TopDish {
    return {
      name: item?.dish_name || '',
      tag: item?.dish_type_name || '',
      restaurant: item?.dish_description || '',
      rating: this.toNumber(item?.rating),
      trendValue: this.toNumber(item?.no_of_people)
    };
  }

  private mapComplianceItems(items: any[]): ComplianceItem[] {
    return items.map((item: any) => ({
      title: item?.title || '',
      description: item?.descriptions || '',
      status: 'updated',
      updatedOn: this.formatUnixDate(item?.updated_at)
    }));
  }

  private mapResourcesItems(items: any[]): ResourceItem[] {
    return items.map((item: any) => ({
      title: item?.title || '',
      points: item?.points || '',
      updatedOn: this.formatUnixDate(item?.updated_at)
    }));
  }

  private groupPlanningCalendar(items: any[]): CalendarMonth[] {
    const grouped: { [month: string]: CalendarEvent[] } = {};

    items.forEach((item: any) => {
      if (!item || !item.date) {
        return;
      }

      const dateObj = new Date(item.date);

      if (isNaN(dateObj.getTime())) {
        return;
      }

      const monthKey = this.getMonthKey(dateObj);

      if (!grouped[monthKey]) {
        grouped[monthKey] = [];
      }

      grouped[monthKey].push({
        day: dateObj.getDate(),
        title: item.event || '',
        type: this.normalizeCalendarType(item.type),
        description: item.descriptions || ''
      });
    });

    // start from first backend month if present, else current month
    const startDate =
      items && items.length && items[0] && items[0].date
        ? new Date(items[0].date)
        : new Date();

    const startMonth = new Date(startDate.getFullYear(), startDate.getMonth(), 1);

    const months: CalendarMonth[] = [];

    for (let i = 0; i < 6; i++) {
      const monthDate = new Date(startMonth.getFullYear(), startMonth.getMonth() + i, 1);
      const monthKey = this.getMonthKey(monthDate);

      months.push({
        month: monthKey,
        events: (grouped[monthKey] || []).sort((a, b) => a.day - b.day)
      });
    }

    return months;
  }

  private getMonthKey(date: Date): string {
    return date.toLocaleString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  }

  private normalizeCalendarType(type: string): 'holiday' | 'event' | 'season' | 'deadline' {
    switch ((type || '').toLowerCase()) {
      case 'holiday':
        return 'holiday';
      case 'season':
        return 'season';
      case 'deadline':
        return 'deadline';
      default:
        return 'event';
    }
  }

  private formatUnixDate(timestamp: string | number): string {
    const value = Number(timestamp);

    if (!value || isNaN(value)) {
      return '-';
    }

    const date = new Date(value * 1000);

    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  }

  private getStringArray(value: any): string[] {
    return Array.isArray(value) ? value.map((item: any) => String(item)) : [];
  }

  private getNumberArray(value: any): number[] {
    return Array.isArray(value) ? value.map((item: any) => this.toNumber(item)) : [];
  }

  private toNumber(value: any): number {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  }

  private getEmptyDashboardPayload(): DashboardPayload {
    return {
      summaryCards: [
        { title: 'Number of Diners', value: 0, subtitle: 'Total diners recorded' },
        { title: 'With Dietary Preferences', value: 0, subtitle: 'Vegan, Vegetarian, Halal, etc.' },
        { title: 'With Food Allergies', value: 0, subtitle: 'Various allergen types' }
      ],
      peopleWithDietaryPreferences: [],
      peopleWithFoodAllergies: [],
      foodAllergyDistribution: [],
      foodPreferenceByCategory: {
        categories: [],
        series: [
          { name: 'Vegan', data: [] },
          { name: 'Vegetarian', data: [] }
        ]
      },
      topCuisineTitle: '',
      topCuisineDishes: [],
      topCategoryTitle: '',
      topCategoryDishes: [],
      complianceItems: [],
      resources: [],
      planningMonths: []
    };
  }

  private createDistributedBarChart(
    items: SimpleChartItem[],
    yAxisTitle: string,
    height = 300
  ): ApexOptions {
    const safeItems = items || [];

    return {
      series: [
        {
          name: yAxisTitle,
          data: safeItems.map(item => item.value)
        }
      ],
      chart: {
        type: 'bar',
        height: height,
        toolbar: { show: false },
        fontFamily: 'Inter, sans-serif'
      },
      noData: {
        text: 'No data available'
      },
      colors: safeItems.map((_, index) => this.chartColors[index % this.chartColors.length]),
      plotOptions: {
        bar: {
          distributed: true,
          borderRadius: 6,
          columnWidth: '52%'
        }
      },
      dataLabels: {
        enabled: false
      },
      legend: {
        show: false
      },
      xaxis: {
        categories: safeItems.map(item => item.label),
        labels: {
          style: {
            fontSize: '12px'
          }
        }
      },
      yaxis: {
        title: {
          text: yAxisTitle
        }
      },
      grid: {
        borderColor: '#E5E7EB',
        strokeDashArray: 4
      },
      tooltip: {
        y: {
          formatter: (value: number) => '' + value
        }
      }
    };
  }

  private createPieChart(items: SimpleChartItem[], height = 300): ApexOptions {
    const safeItems = items || [];

    return {
      series: safeItems.map(item => item.value),
      labels: safeItems.map(item => item.label),
      chart: {
        type: 'pie',
        height: height,
        fontFamily: 'Inter, sans-serif'
      },
      noData: {
        text: 'No data available'
      },
      colors: safeItems.map((_, index) => this.chartColors[index % this.chartColors.length]),
      legend: {
        position: 'bottom',
        fontSize: '12px'
      },
      dataLabels: {
        enabled: true,
        formatter: (value: number) => value.toFixed(0) + '%'
      },
      tooltip: {
        y: {
          formatter: (value: number) => value + '%'
        }
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 280
            },
            legend: {
              position: 'bottom'
            }
          }
        }
      ]
    };
  }

  private createMultiSeriesBarChart(
    input: MultiSeriesChart,
    yAxisTitle: string,
    height = 300
  ): ApexOptions {
    return {
      series: input.series,
      chart: {
        type: 'bar',
        height: height,
        stacked: false,
        toolbar: { show: false },
        fontFamily: 'Inter, sans-serif'
      },
      noData: {
        text: 'No data available'
      },
      colors: input.series.map((_, index) => this.chartColors[index % this.chartColors.length]),
      plotOptions: {
        bar: {
          borderRadius: 6,
          columnWidth: '48%'
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: 0
      },
      xaxis: {
        categories: input.categories
      },
      yaxis: {
        title: {
          text: yAxisTitle
        }
      },
      legend: {
        position: 'bottom'
      },
      grid: {
        borderColor: '#E5E7EB',
        strokeDashArray: 4
      },
      tooltip: {
        shared: true,
        intersect: false
      }
    };
  }
}